import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getCatalog } from '../api/client';
import { cacheImages } from '../lib/cacheImages';
import SiteHeader from './shared/SiteHeader';
import { uiLabels } from '../lib/uiLabels';
import { usePageMeta } from '../lib/usePageMeta';

const IMAGE_BASE = (import.meta.env.VITE_IMAGE_URL ?? '').replace(/\/$/, '');

const PAGE_TITLES = {
  1: 'Ejercicios para Estudiantes',
  4: 'Ejercicios para la Comunidad',
  2: 'Exercises for Students',
};

function imageUrl(path) {
  if (!path) return null;
  const normalized = String(path).replace(/^~\//, '/');
  if (normalized.startsWith('http')) return normalized;
  if (!IMAGE_BASE) return normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${IMAGE_BASE}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
}

function categoryImage(c) {
  if (c.rutaImagen) return imageUrl(c.rutaImagen);
  // Fallback clásico: /images/e_{ID}.png en el servidor de imágenes
  return imageUrl(`/images/e_${c.ID}.png`);
}

export default function Categorias() {
  const { lenguajeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get('test') === '1';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUp, setShowUp] = useState(false);
  const lid = Number(lenguajeId);
  const isCogni = lid === 4;
  usePageMeta(`${PAGE_TITLES[lid] ?? 'Ejercicios'} | LABPSI`);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getCatalog(lid, testMode)
      .then((catalog) => {
        setData(catalog);
        const urls = (catalog?.categorias ?? []).map(categoryImage).filter(Boolean);
        cacheImages(urls);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [lid, testMode]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    setShowUp(false);

    const update = () => {
      const scrollY = window.scrollY || 0;
      const viewH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      const canScroll = docH > viewH + 150;
      setShowUp(canScroll && scrollY + viewH >= docH - 80);
    };
    window.addEventListener('scroll', update, { passive: true });
    requestAnimationFrame(() => requestAnimationFrame(update));
    return () => window.removeEventListener('scroll', update);
  }, [lid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-600">Cargando...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <p className="text-red-600">{error ?? 'Sin datos'}</p>
        <button type="button" onClick={() => navigate('/')} className="ml-4 text-slate-600 underline bg-transparent border-0 cursor-pointer">
          Volver al inicio
        </button>
      </div>
    );
  }

  const { lenguaje, categorias } = data;
  const pageTitle = PAGE_TITLES[lenguaje?.ID] ?? PAGE_TITLES[lid] ?? lenguaje?.Descripcion ?? 'Ejercicios';
  const btnText = (lenguaje?.Textobtn || 'INGRESAR').toUpperCase();
  const themeClass = isCogni ? 'cogni' : 'pedag';
  const labels = uiLabels(lid);

  const goToCategoria = (c) =>
    navigate(`/lenguaje/${lenguajeId}/categoria/${c.ID}${testMode ? '?test=1' : ''}`, {
      state: { categoria: c, lenguaje },
    });

  const goDown = () => {
    window.scrollBy({ top: Math.max(300, window.innerHeight * 0.7), behavior: 'smooth' });
  };
  const goUp = () => {
    window.scrollBy({ top: -Math.max(300, window.innerHeight * 0.7), behavior: 'smooth' });
  };

  return (
    <div className={`page-tipo-test ${themeClass}`}>
      <div className="navbar-wrapper">
        <SiteHeader lenguajeId={lid} />

        <div className="tipo-test-title-row">
          <button type="button" className="btn-back" onClick={() => navigate('/')}>
            {labels.back}
          </button>
          <h1 className="title-pag">
            <span className="title-page-content">{pageTitle}</span>
          </h1>
        </div>
      </div>

      <div className="marketing">
        <div className="sections-container">
          <div className="tipo-test-grid">
            {categorias.map((c) => (
              <a
                key={c.ID}
                href={`/lenguaje/${lenguajeId}/categoria/${c.ID}${testMode ? '?test=1' : ''}`}
                className="test-item"
                onClick={(e) => {
                  e.preventDefault();
                  goToCategoria(c);
                }}
              >
                <div className={`item-content ${isCogni ? 'ej-cogni' : 'ej-pedag'}`}>
                  <img
                    className="img-circle"
                    src={categoryImage(c)}
                    alt=""
                    decoding="async"
                    fetchPriority="high"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/assets/img/logo.png';
                    }}
                  />
                  <div className="vertical-center">{c.Descripcion}</div>
                  <span className="btn-ingresar">{btnText}</span>
                </div>
              </a>
            ))}
          </div>

          <div className="tipo-test-footer">
            <button
              type="button"
              className="icono-home-new"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img src="/assets/img/flecha-volver.png" alt="" />
              <br />{labels.backTopLine1}
              <br />{labels.backTopLine2}
            </button>
          </div>
        </div>
      </div>

      {!showUp && (
        <button type="button" className="icono-abajo" onClick={goDown} aria-label="Desplazar hacia abajo">
          <img src="/assets/img/abajoNew.png" alt="" />
        </button>
      )}
      {showUp && (
        <button type="button" className="icono-arriba" onClick={goUp} aria-label="Desplazar hacia arriba">
          <img src="/assets/img/arribaNew.png" alt="" />
        </button>
      )}
    </div>
  );
}
